// Octavio Tequila — behaviour for .oct-* sections
import { CartLinesUpdateEvent } from '@shopify/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Reveal on scroll ---------- */
function initReveal(root = document) {
  const els = root.querySelectorAll('.oct [data-reveal]:not(.is-in), .ot [data-ot-r]:not(.in)');
  if (!els.length) return;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in', 'in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in', 'in');
          io.unobserve(e.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  els.forEach((el) => io.observe(el));
}

/* ---------- Count-up numbers ---------- */
function initCounters(root = document) {
  const els = root.querySelectorAll('[data-count]:not([data-counted])');
  if (!els.length) return;
  const run = (el) => {
    el.dataset.counted = '1';
    const to = parseFloat(el.dataset.count);
    if (reduceMotion || Number.isNaN(to)) {
      el.textContent = el.dataset.count;
      return;
    }
    const dur = 1800;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 4);
      el.textContent = Math.round(to * eased).toString();
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          run(e.target);
          io.unobserve(e.target);
        }
      }
    },
    { threshold: 0.5 }
  );
  els.forEach((el) => {
    el.textContent = '0';
    io.observe(el);
  });
}

/* ---------- Videos: play only when visible ---------- */
function initVideos(root = document) {
  const vids = root.querySelectorAll('.oct video[data-autoplay]');
  if (!vids.length) return;
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const v = /** @type {HTMLVideoElement} */ (e.target);
      if (e.isIntersecting && !reduceMotion) v.play().catch(() => {});
      else v.pause();
    }
  });
  vids.forEach((v) => {
    v.muted = true;
    io.observe(v);
  });
}

/* ---------- Add to cart (talks to Horizon's cart drawer) ---------- */

/* Open the slide cart only once its contents are rendered — no empty flash, no reflow mid-slide.
   (Theme auto-open is OFF; this is the single opener.) */
function openDrawerWhenReady(expectedCount) {
  const comp = document.querySelector('cart-drawer-component');
  const drawer = comp?.closest('theme-drawer');
  if (!comp || !drawer) return Promise.resolve();
  const ready = () => {
    if (comp.querySelector('.cart-drawer--empty')) return false;
    const badge = comp.querySelector('.cart-bubble__text-count, [data-cart-count]');
    const rows = comp.querySelectorAll('[data-key], .cart-items__table-row, .cart-item').length;
    return rows > 0 || (badge && Number(badge.textContent) >= expectedCount);
  };
  return new Promise((resolve) => {
    const go = () => {
      mo.disconnect(); clearTimeout(t);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!drawer.isOpen) drawer.open();
        resolve();
      }));
    };
    const mo = new MutationObserver(() => { if (ready()) go(); });
    mo.observe(comp, { childList: true, subtree: true });
    const t = setTimeout(go, 1600);
    if (ready()) setTimeout(() => { if (ready()) go(); }, 60);
  });
}

async function addToCart(trigger, lines) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  trigger.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'octavio', sections: data.sections, didError: Boolean(data.status) },
    });
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    await openDrawerWhenReady(cart.item_count);
    return true;
  } catch (err) {
    deferred.reject?.(err);
    console.warn('[octavio] add to cart failed', err);
    return false;
  }
}

document.addEventListener('click', async (event) => {
  const btn = /** @type {HTMLElement|null} */ (event.target instanceof Element ? event.target.closest('[data-oct-add]') : null);
  if (!btn) return;
  event.preventDefault();
  const ids = (btn.dataset.octAdd || '').split(',').filter(Boolean);
  if (!ids.length) return;
  const label = btn.querySelector('[data-label]');
  const original = label?.textContent;
  btn.classList.add('is-loading');
  if (label) label.textContent = 'Adding…';
  const ok = await addToCart(btn, ids.map((id) => ({ id: Number(id), quantity: Number(btn.dataset.qty || 1) })));
  btn.classList.remove('is-loading');
  if (label) {
    label.textContent = ok ? btn.dataset.addedText || 'Added' : btn.dataset.errorText || 'Unavailable';
    btn.classList.toggle('is-added', ok);
    setTimeout(() => {
      label.textContent = original;
      btn.classList.remove('is-added');
    }, 2400);
  }
});

/* ---------- Rail arrows ---------- */
document.addEventListener('click', (event) => {
  const b = event.target instanceof Element ? event.target.closest('[data-rail-dir]') : null;
  if (!b) return;
  const rail = document.getElementById(b.getAttribute('aria-controls') || '');
  if (!rail) return;
  const card = rail.firstElementChild;
  const step = card ? card.getBoundingClientRect().width + 20 : rail.clientWidth * 0.8;
  rail.scrollBy({ left: Number(b.dataset.railDir) * step, behavior: reduceMotion ? 'auto' : 'smooth' });
});

/* ---------- Quiz ---------- */
function initQuiz(root = document) {
  root.querySelectorAll('[data-oct-quiz]').forEach((quiz) => {
    if (quiz.dataset.ready) return;
    quiz.dataset.ready = '1';
    const steps = [...quiz.querySelectorAll('.oct-quiz__step')];
    const bars = [...quiz.querySelectorAll('.oct-quiz__progress span')];
    const results = [...quiz.querySelectorAll('.oct-quiz__result')];
    let score = { gold: 0, cristalino: 0 };
    let i = 0;
    const show = (n) => {
      steps.forEach((s, k) => s.classList.toggle('is-active', k === n));
      bars.forEach((b, k) => b.classList.toggle('is-on', k <= n));
    };
    quiz.addEventListener('click', (e) => {
      const opt = e.target instanceof Element ? e.target.closest('[data-vote]') : null;
      if (opt) {
        score[opt.dataset.vote] += 1;
        i += 1;
        if (i < steps.length) show(i);
        else {
          steps.forEach((s) => s.classList.remove('is-active'));
          bars.forEach((b) => b.classList.add('is-on'));
          const pick = score.gold > score.cristalino ? 'gold' : 'cristalino';
          results.forEach((r) => r.classList.toggle('is-active', r.dataset.result === pick));
        }
        return;
      }
      if (e.target instanceof Element && e.target.closest('[data-quiz-restart]')) {
        score = { gold: 0, cristalino: 0 };
        i = 0;
        results.forEach((r) => r.classList.remove('is-active'));
        show(0);
      }
    });
    show(0);
  });
}

/* ---------- Scrollytelling steps ---------- */
function initSteps(root = document) {
  root.querySelectorAll('[data-oct-steps]').forEach((wrap) => {
    if (wrap.dataset.ready) return;
    wrap.dataset.ready = '1';
    const imgs = [...wrap.querySelectorAll('.oct-steps__stage img')];
    const steps = [...wrap.querySelectorAll('.oct-step')];
    const count = wrap.querySelector('[data-step-count]');
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const n = steps.indexOf(/** @type {HTMLElement} */ (e.target));
          steps.forEach((s, k) => s.classList.toggle('is-on', k === n));
          imgs.forEach((im, k) => im.classList.toggle('is-on', k === n));
          if (count) count.textContent = String(n + 1).padStart(2, '0');
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    steps.forEach((s) => io.observe(s));
  });
}

/* ---------- Recipe: servings toggle ---------- */
const FRACTIONS = { '½': 0.5, '¼': 0.25, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3 };
function fmt(n) {
  const whole = Math.floor(n);
  const rest = +(n - whole).toFixed(2);
  const map = { 0.25: '¼', 0.5: '½', 0.75: '¾', 0.33: '⅓', 0.67: '⅔' };
  if (rest === 0) return String(whole);
  if (map[rest]) return (whole ? whole : '') + map[rest];
  return String(+n.toFixed(1));
}
function scaleLine(text, factor) {
  return text.replace(/^(\d+(?:\.\d+)?)?\s*([½¼¾⅓⅔])?/, (m, num, frac) => {
    if (!num && !frac) return m;
    const val = (num ? parseFloat(num) : 0) + (frac ? FRACTIONS[frac] : 0);
    return fmt(val * factor) + (m.endsWith(' ') ? ' ' : '');
  });
}
document.addEventListener('click', (e) => {
  const b = e.target instanceof Element ? e.target.closest('[data-serves]') : null;
  if (!b) return;
  const box = b.closest('[data-recipe]');
  if (!box) return;
  const base = Number(box.dataset.recipe) || 2;
  const factor = Number(b.dataset.serves) / base;
  box.querySelectorAll('[data-serves]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
  box.querySelectorAll('[data-ing]').forEach((el) => {
    el.textContent = scaleLine(el.dataset.ing || '', factor);
  });
});

/* ---------- Age gate ---------- */
function initAgeGate() {
  const gate = document.getElementById('oct-age');
  if (!gate || window.Shopify?.designMode) return;
  let ok = false;
  try {
    ok = localStorage.getItem('oct-age-ok') === '1';
  } catch (_) {}
  if (ok) {
    document.documentElement.classList.remove('oct-age-pending');
    return;
  }
  gate.hidden = false;
  document.documentElement.classList.add('oct-age-lock', 'oct-age-pending');
  gate.querySelector('[data-age-yes]')?.focus();
  gate.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('[data-age-yes]')) {
      try {
        localStorage.setItem('oct-age-ok', '1');
      } catch (_) {}
      gate.hidden = true;
      document.documentElement.classList.remove('oct-age-lock', 'oct-age-pending');
    } else if (e.target.closest('[data-age-no]')) {
      const msg = gate.querySelector('.oct-age__no');
      if (msg) msg.style.display = 'block';
    }
  });
}

/* ---------- Gentle parallax on origin backgrounds ---------- */
function initParallax() {
  if (reduceMotion) return;
  const els = [...document.querySelectorAll('[data-parallax]')];
  if (!els.length) return;
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    for (const el of els) {
      const r = el.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) continue;
      const p = (r.top + r.height / 2 - vh / 2) / vh;
      el.style.transform = `translate3d(0, ${(p * -6).toFixed(2)}%, 0)`;
    }
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  update();
}

function initAll(root = document) {
  initReveal(root);
  initCounters(root);
  initVideos(root);
  initQuiz(root);
  initSteps(root);
}

/* ---------- Keep scroll position across Horizon's 990px scroller switch ----------
   Below 990px the window scrolls; at 990px+ html/body lock and .page-wrapper scrolls.
   Resizing across that line left the window stuck at its old offset (black band at the bottom). */
function initScrollerSwap() {
  const pw = document.querySelector('.page-wrapper');
  if (!pw) return;
  const mq = window.matchMedia('(min-width: 990px)');
  mq.addEventListener('change', (e) => {
    if (e.matches) {
      const y = window.scrollY;
      window.scrollTo({ top: 0, behavior: 'instant' });
      pw.scrollTo({ top: y, behavior: 'instant' });
    } else {
      const y = pw.scrollTop;
      pw.scrollTo({ top: 0, behavior: 'instant' });
      window.scrollTo({ top: y, behavior: 'instant' });
    }
  });
}

initAll();
initAgeGate();
initScrollerSwap();
// parallax disabled: Horizon scrolls inside .page-wrapper, window scroll never fires

document.addEventListener('shopify:section:load', (e) => initAll(/** @type {HTMLElement} */ (e.target)));
